import React, { useMemo, useState } from "react";
import { Camera, X, ChevronRight, ImageIcon } from "lucide-react";
import CameraCapture from "./CameraCapture";

export const PHOTO_GUIDE = [
  { key: "fahrzeugschein",    title: "Fahrzeugschein",       desc: "Aufgefaltet und gut beleuchtet" },
  { key: "vorne_rechts",      title: "Vorne rechts",         desc: "Eckaufnahme — Front + rechte Seite aus 3/4-Perspektive" },
  { key: "front",             title: "Front",                desc: "Frontansicht — Fahrzeug mittig, beide Scheinwerfer, Kennzeichen" },
  { key: "vorne_links",       title: "Vorne links",          desc: "Eckaufnahme — Front + linke Seite aus 3/4-Perspektive" },
  { key: "seite_rechts",      title: "Seitenansicht rechts", desc: "Komplette rechte Fahrzeugseite frontal aufgenommen" },
  { key: "hinten_rechts",     title: "Hinten rechts",        desc: "Eckaufnahme — Heck + rechte Seite aus 3/4-Perspektive" },
  { key: "heck",              title: "Heck",                 desc: "Heck mittig & gerade — Kennzeichen lesbar" },
  { key: "hinten_links",      title: "Hinten links",         desc: "Eckaufnahme — Heck + linke Seite aus 3/4-Perspektive" },
  { key: "seite_links",       title: "Seitenansicht links",  desc: "Komplette linke Fahrzeugseite frontal aufgenommen" },
  { key: "schadenbereich",    title: "Schadendetails (weit)",desc: "Schaden im Kontext — gesamte Karosserie-Sektion sichtbar" },
  { key: "schadenstelle",     title: "Schadendetails (Mitte)", desc: "Mittlere Nahaufnahme — Schaden im direkten Umfeld" },
  { key: "schadendetails",    title: "Schadendetails (nah)", desc: "Extreme Detail-Aufnahme — Material, Tiefe, Kanten klar erkennbar" },
  { key: "mehrere_schaden",   title: "Mehrere Schadenbilder", desc: "Sammelbild mehrerer Schadenstellen — Übersicht" },
  { key: "reifen",            title: "Reifen",               desc: "Reifen + Felge mit Profil & evtl. Beschädigungen sichtbar" },
  { key: "kilometerstand",    title: "Kilometerstand",       desc: "Zündung einschalten und Kilometerstand am Tacho fotografieren" },
  { key: "kennzeichen",       title: "Kennzeichen",          desc: "Amtl. Kennzeichen scharf & gut lesbar" },
  { key: "innenraum",         title: "Innenraum",            desc: "Armaturenbrett + Cockpit komplett sichtbar" },
  { key: "fahrgestellnummer", title: "Fahrgestellnummer",    desc: "VIN-Plakette an der Fahrertür oder am Motorraum" },
  { key: "unfallbericht",     title: "Unfallbericht",        desc: "Polizei- oder Versicherungsdokument zum Vorgang" },
];

/**
 * PhotoGuide — read-only Beispiel-Galerie. Kein eigener Auslöser-Button:
 * die Kamera wird über den Bottom-Nav-Foto-Button (mobil) bzw. das kleine
 * Kamera-Icon im Step-Header (Desktop) gestartet. Vom Parent kommt ein
 * `cameraTrigger`-Counter; jede Erhöhung öffnet die Multi-Kategorie-Kamera.
 */
export default function PhotoGuide({ caseId, photos = [], onChange, cameraTrigger = 0 }) {
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [initialCategory, setInitialCategory] = useState(null);

  // External trigger: parent bumps this number → open camera
  React.useEffect(() => {
    if (cameraTrigger > 0) {
      setInitialCategory(null);
      setCameraOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraTrigger]);

  const countsByCategory = useMemo(
    () => (photos || []).reduce((acc, p) => {
      const k = p.category || "_uncat";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {}),
    [photos]
  );

  const handleUploaded = (newPhotos) => {
    onChange?.([...(photos || []), ...newPhotos]);
  };

  const totalDone = PHOTO_GUIDE.filter((g) => countsByCategory[g.key] > 0).length;
  const progressPct = Math.round((totalDone / PHOTO_GUIDE.length) * 100);

  const startCamera = (category = null) => {
    if (!caseId) return;
    setInitialCategory(category);
    setCameraOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="photo-guide">
      {/* Header + progress */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-zinc-500">Beispielfotos</div>
          <p className="text-sm text-zinc-600 mt-1 max-w-xl">
            So sollten deine Aufnahmen aussehen. Tippe eine Kachel für die Vergrößerung — der Auslöser ist unten.
          </p>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold brand-gradient-text leading-none" data-testid="photo-guide-counter">
            {totalDone}<span className="text-zinc-300">/{PHOTO_GUIDE.length}</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mt-0.5">abgedeckt</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div
          className="h-full brand-gradient-bg transition-all"
          style={{ width: `${progressPct}%` }}
          data-testid="photo-guide-progress"
        />
      </div>

      {/* Reference tiles — tap-to-preview ONLY, no inline camera */}
      <div className="relative -mx-5 lg:-mx-10">
        <div className="px-5 lg:px-10 overflow-x-auto scroll-smooth">
          <div className="flex gap-3 py-3 min-w-min" data-testid="photo-guide-strip">
            {PHOTO_GUIDE.map((g) => {
              const count = countsByCategory[g.key] || 0;
              const done = count > 0;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setPreview(g)}
                  data-testid={`photo-guide-card-${g.key}`}
                  className={`group relative shrink-0 w-[140px] sm:w-[150px] rounded-xl border bg-white overflow-hidden cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md text-left ${
                    done ? "border-emerald-300 ring-1 ring-emerald-100" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <div
                    className="relative aspect-[4/3] flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: "rgb(223,223,223)" }}
                  >
                    <img
                      src={`/photo-examples/${g.key}.png`}
                      alt={g.title}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const ph = e.currentTarget.nextElementSibling;
                        if (ph && ph.dataset.placeholder) ph.style.display = "flex";
                      }}
                    />
                    <div
                      data-placeholder="true"
                      className="absolute inset-0 flex-col items-center justify-center gap-1 text-zinc-300 hidden"
                    >
                      <ImageIcon className="h-7 w-7" strokeWidth={1.4} />
                      <span className="text-[9px] font-bold tracking-widest uppercase">Kein Beispiel</span>
                    </div>
                    {/* Top-left "done"-counter, no inline camera button */}
                    {count > 0 && (
                      <div
                        className="absolute top-1.5 left-1.5 h-5 min-w-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
                        data-testid={`photo-guide-count-${g.key}`}
                      >
                        ✓ {count}
                      </div>
                    )}
                    <div className="absolute bottom-1.5 right-1.5 h-7 px-2 rounded-md bg-black/55 backdrop-blur text-white text-[10px] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="h-3 w-3" />
                      Vergrößern
                    </div>
                  </div>
                  <div className="p-2.5 min-h-[72px]">
                    <div className="font-display text-[12px] font-semibold tracking-tight text-zinc-900 leading-tight line-clamp-1">
                      {g.title}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1 leading-snug line-clamp-3">{g.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none" />
        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none" />
      </div>

      {/* Lightbox preview */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="photo-guide-lightbox"
        >
          <div
            className="max-w-xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-zinc-50">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-100 z-10"
                data-testid="photo-guide-lightbox-close"
              >
                <X className="h-4 w-4" />
              </button>
              <img
                src={`/photo-examples/${preview.key}.png`}
                alt={preview.title}
                className="max-h-[55vh] w-full object-contain"
              />
            </div>
            <div className="p-5 border-t border-zinc-200 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">Referenz-Aufnahme</div>
                <div className="font-display text-xl font-bold tracking-tight mt-1">{preview.title}</div>
              </div>
              <p className="text-sm text-zinc-600">{preview.desc}</p>
              <button
                type="button"
                onClick={() => { setPreview(null); startCamera(preview.key); }}
                disabled={!caseId}
                className="brand-gradient-bg text-white w-full py-3 rounded-xl font-semibold hover:shadow-[0_0_24px_rgba(16,185,129,0.4)] transition flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="photo-guide-lightbox-capture"
              >
                <Camera className="h-5 w-5" strokeWidth={1.75} />
                Mit dieser Kategorie starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live camera modal — multi-category burst */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => { setCameraOpen(false); setInitialCategory(null); }}
        caseId={caseId}
        categories={PHOTO_GUIDE}
        initialCategory={initialCategory}
        onUploaded={handleUploaded}
      />
    </div>
  );
}
